import { RefObject, useContext, useEffect } from 'react';
import { Text, ArrowRight, Button, FillTag } from '@doda/io-react/common';
import {
  useChange,
  useEscapeClose,
} from '@doda/common/src/components/DesiredConditionsModal/logics/hook';
import {
  handleCancel,
  handleSubmitIncome,
} from '@doda/common/src/components/DesiredConditionsModal/logics/event';
import { SalaryContext } from '@doda/common/src/components/DesiredConditionsModal/logics/context';
import { Income as Salaries } from '@doda/common/src/components/DesiredConditionsModal/type';
import styles from './index.module.scss';
import { CancelButton } from '../CancelButton';
import { BackButton } from '../BackButton';
import dialogStyles from '../../index.module.scss';

/**
 * 希望年収選択ダイアログのProps
 */
interface Props {
  /**
   * ダイアログ参照情報
   */
  selefDialogReference: RefObject<HTMLDialogElement>;
  /**
   * 制御するダイアログ参照情報
   */
  controlledDialogReference: RefObject<HTMLDialogElement>;
  /**
   * 希望年収データ
   */
  salaries: Salaries[];
  /**
   * 初回のヒアリング状態で起動するかどうか
   *
   * 初回のヒアリング状態にするならtrueを指定する
   */
  startupMode: boolean;
}

/**
 * 希望年収選択ダイアログ
 *
 * @param {Props} props 構築情報
 */
export const Income: React.FC<Props> = ({
  selefDialogReference,
  controlledDialogReference,
  salaries,
  startupMode,
}: Props) => {
  const { selectedItem, toggleSelection, setItem } = useChange({
    name: '選択してください',
    value: '',
  });
  const [salary, setSalary] = useContext(SalaryContext);
  useEscapeClose(selefDialogReference, controlledDialogReference);

  useEffect(() => {
    setItem(salary);
  }, [salary, setItem]);

  return (
    <dialog
      id="income"
      ref={selefDialogReference}
      className={dialogStyles.dialog}
    >
      <div className={dialogStyles.dialog__header}>
        <Text
          color="black_pc"
          as="span"
          size="14--articlePC"
          className={styles[`income-dialog__header__text`]}
        >
          <Text
            color="black_pc"
            as="span"
            weight="bold"
            size="14--articlePC"
            className={styles[`income-dialog__header__middle-text`]}
          >
            希望の年収
          </Text>
          を教えてください
        </Text>

        <div className={dialogStyles.dialog__header__right}>
          <CancelButton
            onClick={() => {
              handleCancel(
                () => selefDialogReference.current?.close(),
                () => controlledDialogReference.current?.close()
              );
            }}
          />
        </div>
      </div>

      <div
        className={`${dialogStyles.dialog__layout} ${
          dialogStyles[`selection-dialog__layout`]
        }`}
      >
        <div className={dialogStyles[`selection-dialog__content-upper`]}>
          <BackButton
            onClick={() => {
              selefDialogReference.current?.close();
            }}
          />
        </div>

        <ul
          className={`${dialogStyles.dialog__content} ${
            dialogStyles[`selection-dialog__content`]
          }`}
        >
          {salaries.map((income, index) => {
            return (
              <li key={income.name}>
                <button
                  className={styles[`income-dialog__content-item`]}
                  type="button"
                  key={index}
                  onClick={() => {
                    handleSubmitIncome(
                      () => selefDialogReference.current?.close(),
                      toggleSelection({
                        name: income.name,
                        value: income.value,
                      }),
                      setSalary,
                      startupMode
                    );
                  }}
                >
                  <Text color="black_pc">{income.name}</Text>
                  <div className={styles[`income-dialog__content-item--right`]}>
                    {salary.name === income.name && (
                      <FillTag color="lightGreen">選択中</FillTag>
                    )}
                    <ArrowRight color="black" size="M" />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className={dialogStyles.dialog__bottom}>
        <Button
          size="M"
          variant="green"
          as="button"
          onClick={() => {
            selefDialogReference.current?.close();
            setSalary(selectedItem);
          }}
          className={dialogStyles.dialog__bottom__item}
        >
          設定する
        </Button>
      </div>
    </dialog>
  );
};
